// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import HeroImage from '../assets/hero-image.jpeg';
import BackgroundImage from '../assets/background-image.jpg';
import TestimonialImage from '../assets/testimonial-image.jpg';
import WhatsAppIcon from '../assets/whatsapp-icon.png';
import { apiUrl } from '../utils/api';

// --- Static fallback data ---
const staticHeroData = {
  title: "Meet Dr. Ashweeja Nagesh, Your Trusted Psychiatrist",
  description: "Dr. Ashweeja Nagesh is dedicated to providing compassionate and professional mental health care, tailored to each individual's unique needs.",
  image: HeroImage,
  background: BackgroundImage,
};

const staticAboutData = {
  header: "About Dr. Ashweeja Nagesh",
  description: "Dr. Ashweeja Nagesh is a highly experienced psychiatrist with a passion for helping individuals manage their mental health and emotional well-being. With years of experience, she has helped numerous clients lead happier and more fulfilled lives.",
};

const staticProfileSections = [
  {
    title: "📍 Current Position",
    items: [
      "Consultant Psychiatrist, Punjab",
      "Online Mental Health Practitioner",
    ],
  },
  {
    title: "🏥 Previous Experience",
    items: [
      "Resident Psychiatrist - Meenakshi Medical College",
      "Consultant Psychiatrist - MindPlus Hospital, Punjab",
    ],
  },
  {
    title: "🎓 Education",
    items: [
      "MBBS - Hassan Institute Of Medical Sciences (HIMS), Karnataka",
      "MD Psychiatry - Meenakshi Medical College Hospital And Research Institute, Tamil Nadu",
    ],
  },
];

const staticServices = [
  {
    name: "Anxiety & Depression Treatment",
    description: "Professional support tailored for anxiety & depression treatment needs.",
  },
  {
    name: "Child & Adolescent Psychiatry",
    description: "Professional support tailored for child & adolescent psychiatry needs.",
  },
  {
    name: "Addiction Counseling",
    description: "Professional support tailored for addiction counseling needs.",
  },
  {
    name: "Marital & Family Therapy",
    description: "Professional support tailored for marital & family therapy needs.",
  },
  {
    name: "Sleep & Stress Management",
    description: "Professional support tailored for sleep & stress management needs.",
  },
  {
    name: "Online Consultations",
    description: "Professional support tailored for online consultations needs.",
  },
];

const Home = () => {
  const [heroData, setHeroData] = useState(staticHeroData);
  const [aboutData, setAboutData] = useState(staticAboutData);
  const [profileSections, setProfileSections] = useState(staticProfileSections);
  const [services, setServices] = useState(staticServices);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [message, setMessage] = useState('');

  // Fetch all homepage data from your API
  const fetchAllData = async () => {
    try {
      const res = await fetch(apiUrl('/api/homepage'));
      if (!res.ok) throw new Error('API error');
      const data = await res.json();

      // Use API data if available, otherwise fallback to static
      setHeroData({
        ...staticHeroData,
        ...data.heroData,
        image: data.heroData?.image ? data.heroData.image : staticHeroData.image,
        background: data.heroData?.background ? data.heroData.background : staticHeroData.background,
      });
      setAboutData({ ...staticAboutData, ...data.aboutData });
      setProfileSections(Array.isArray(data.profileSections) && data.profileSections.length > 0 ? data.profileSections : staticProfileSections);
      setServices(Array.isArray(data.services) && data.services.length > 0 ? data.services : staticServices);
    } catch (err) {
      // On error, keep static data
      setHeroData(staticHeroData);
      setAboutData(staticAboutData);
      setProfileSections(staticProfileSections);
      setServices(staticServices);
    }
  };

  // Fetch testimonials from the API
  const fetchTestimonials = async () => {
    try {
      const response = await fetch(apiUrl('/api/testimonials'));
      if (!response.ok) throw new Error('Failed to fetch testimonials');
      const data = await response.json();
      setTestimonials(data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);

      // Fallback to static testimonials
      setTestimonials([
        {
          id: 1,
          name: 'John Doe',
          message: 'Dr. Ashweeja Nagesh is amazing! She helped me overcome my anxiety.',
          rating: 5,
        },
        {
          id: 2,
          name: 'Jane Smith',
          message: 'The best psychiatrist I have ever met. Highly recommended!',
          rating: 4,
        },
        {
          id: 3,
          name: 'Michael Johnson',
          message: 'Very professional and compassionate. Thank you for your help!',
          rating: 5,
        },
        {
          id: 4,
          name: 'Emily Davis',
          message: 'Dr. Nagesh truly cares about her patients. I feel so much better now.',
          rating: 5,
        },
        {
          id: 5,
          name: 'Chris Brown',
          message: 'The therapy sessions were life-changing. Highly recommend her services.',
          rating: 4,
        },
        {
          id: 6,
          name: 'Sophia Wilson',
          message: 'Dr. Ashweeja is very understanding and listens to all your concerns.',
          rating: 5,
        },
        {
          id: 7,
          name: 'Liam Martinez',
          message: 'I feel much more confident and happy after my sessions with Dr. Nagesh.',
          rating: 5,
        },
        {
          id: 8,
          name: 'Olivia Garcia',
          message: 'She is very knowledgeable and provides excellent care.',
          rating: 4,
        },
        {
          id: 9,
          name: 'Noah Rodriguez',
          message: 'Dr. Nagesh helped me manage my stress and improve my mental health.',
          rating: 5,
        },
        {
          id: 10,
          name: 'Ava Hernandez',
          message: 'I am so grateful for the support and guidance I received.',
          rating: 5,
        },
        {
          id: 11,
          name: 'Ethan Lee',
          message: 'Dr. Ashweeja is a great listener and provides practical advice.',
          rating: 4,
        },
        {
          id: 12,
          name: 'Isabella Walker',
          message: 'She made me feel comfortable and safe during every session.',
          rating: 5,
        },
        {
          id: 13,
          name: 'Mason Hall',
          message: 'I highly recommend Dr. Nagesh for anyone seeking mental health support.',
          rating: 5,
        },
        {
          id: 14,
          name: 'Mia Allen',
          message: 'Her approach is very professional and effective.',
          rating: 4,
        },
        {
          id: 15,
          name: 'Lucas Young',
          message: 'Dr. Ashweeja helped me regain control of my life. Thank you!',
          rating: 5,
        },
      ]);
    }
  };

  useEffect(() => {
    fetchAllData();
    fetchTestimonials();
  }, []);

  const handleNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === testimonials.length - 4 ? 0 : prevIndex + 1
    );
  };

  const handlePrevTestimonial = () => {
    setCurrentTestimonialIndex(
      (prevIndex) => (prevIndex === 0 ? testimonials.length - 4 : prevIndex - 1)
    );
  };

  const handleBookService = (serviceName) => {
    navigate(`/book-appointment?reason=${encodeURIComponent(serviceName)}`);
  };

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNextTestimonial,
    onSwipedRight: handlePrevTestimonial,
    preventScrollOnSwipe: true,
    trackMouse: true, // Enables mouse dragging
  });

  const handleOpenChat = () => {
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setWhatsAppNumber('');
    setMessage('');
  };

  const handleSendMessage = () => {
    const doctorWhatsAppNumber = '+916284594192'; // Replace with the doctor's WhatsApp number
    const encodedMessage = encodeURIComponent(message.trim());
    
    if (!doctorWhatsAppNumber) {
      alert('WhatsApp number is missing.');
      return;
    }
  
    const whatsappURL = `https://api.whatsapp.com/send?phone=${doctorWhatsAppNumber}&text=${encodedMessage}`;
    window.open(whatsappURL, '_blank'); // Open WhatsApp in a new tab
    handleCloseChat();
  };

  // Static values for button text and link
  const heroButtonText = "Book an Appointment";
  const heroButtonLink = "/book-appointment";

  return (
    <div className="font-sans">
      {/* WhatsApp Icon */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleOpenChat}
          className="w-60 h-20 bg-transparent hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition duration-300 flex items-center justify-center"
          aria-label="Open WhatsApp Chat"
        >
          <img src={WhatsAppIcon} alt="WhatsApp" className="w-60 h-20" />
        </button>
      </div>

      {/* WhatsApp Chat Modal */}
      {isChatOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-xl font-semibold mb-4">Send a WhatsApp Message</h2>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Your WhatsApp Number</label>
              <input
                type="tel"
                value={whatsAppNumber}
                onChange={(e) => setWhatsAppNumber(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="Enter your WhatsApp number"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-2">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
                rows="4"
                placeholder="Enter your message"
              ></textarea>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCloseChat}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-32 px-6"
        style={{ backgroundImage: `url(${heroData.background})` }}
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 container mx-auto flex flex-col-reverse md:flex-row items-center justify-center space-y-6 md:space-y-0">
          <div className="text-white text-center md:text-left max-w-lg md:max-w-xl md:ml-16 space-y-6">
            <h1 className="text-4xl md:text-5xl font-semibold">{heroData.title}</h1>
            <p className="text-lg md:text-xl">{heroData.description}</p>
            <Link
              // to={heroButtonLink}
              onClick={handleOpenChat}
              className="inline-block px-8 py-4 bg-blue-600 hover:bg-blue-700 transition-colors duration-300 text-white rounded-lg text-xl shadow-lg"
            >
              {heroButtonText}
            </Link>
          </div>
          <div
            className="w-72 h-72 md:w-96 md:h-96 bg-cover bg-center rounded-full border-8 border-white shadow-lg"
            style={{ backgroundImage: `url(${heroData.image})` }}
          />
        </div>
      </section>

      {/* About Section */}
      <section className="about py-16 bg-gray-100 px-4">
        <div className="container mx-auto flex flex-col md:flex-row items-start">
          {/* About Section: Appears First on Mobile */}
          <div className="w-full md:w-3/4 md:pl-8 order-1 md:order-2">
            <h2 className="text-3xl font-semibold mb-4 text-left">{aboutData.header}</h2>
            <p className="text-lg text-gray-700 mb-6">{aboutData.description}</p>
          </div>
          {/* Profile Sections */}
          <div className="flex flex-col space-y-6 w-full md:w-1/4 pr-4 order-2 md:order-1">
            {profileSections.map((section, idx) => (
              <div key={idx}>
                <h3 className="text-xl font-bold text-blue-800 mb-2">{section.title}</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  {section.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-semibold mb-10">Our Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="p-6 bg-blue-50 rounded-xl shadow hover:shadow-lg transition flex flex-col justify-between"
              >
                <div>
                  <h3 className="text-xl font-bold text-blue-700 mb-2">{service.name}</h3>
                  <p className="text-gray-700 mb-4">{service.description}</p>
                </div>
                <button
                  onClick={() => handleBookService(service.name)}
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Book Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials py-16 px-4 bg-white" {...swipeHandlers}>
        <div className="text-center mb-10">
          <h2 className="text-3xl font-semibold mb-2">What Our Patients Say</h2>
        </div>
        <div className="relative">
          {/* Left Arrow - Hidden on Mobile */}
          <button
            className="absolute left-0 p-4 text-white bg-blue-700 rounded-full shadow-lg z-10 top-1/2 transform -translate-y-1/2 hidden md:block"
            onClick={handlePrevTestimonial}
          >
            &#60;
          </button>

          {/* Testimonials Carousel */}
          <div className="testimonial-container flex gap-6 px-2 sm:px-4 overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentTestimonialIndex * 25}%)` }}
            >
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="testimonial p-4 sm:p-6 bg-white rounded-xl shadow-md text-center flex flex-col items-center justify-center transition-transform transform hover:scale-105 hover:shadow-2xl hover:bg-gray-100"
                  style={{ minWidth: '25%' }}
                >
                  <img
                    src={TestimonialImage} // Replace with the correct image source
                    alt="Client"
                    className="w-16 h-16 rounded-full mb-4 border-2 border-blue-500"
                  />
                  <div className="text-yellow-400 mb-2 text-lg">
                    {'★'.repeat(testimonial.rating)}{'☆'.repeat(5 - testimonial.rating)}
                  </div>
                  <p className="text-gray-700 italic mb-2 text-center">"{testimonial.message}"</p>
                  <p className="text-sm font-semibold text-gray-800 text-center">- {testimonial.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow - Hidden on Mobile */}
          <button
            className="absolute right-0 p-4 text-white bg-blue-700 rounded-full shadow-lg z-10 top-1/2 transform -translate-y-1/2 hidden md:block"
            onClick={handleNextTestimonial}
          >
            &#62;
          </button>
        </div>
        <div className="text-center mt-8">
          <Link
            to="/testimonials"
            className="inline-block px-8 py-4 bg-blue-600 hover:bg-blue-700 transition-colors duration-300 text-white rounded-lg text-xl shadow-lg"
          >
            Write a Testimonial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white text-center py-6">
        <p className="text-sm">&copy; {new Date().getFullYear()} Dr. Ashweeja Nagesh Clinic. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Home;
